import React, { useCallback, useState, useEffect, KeyboardEvent, useImperativeHandle, forwardRef } from 'react';

import Modal from '@material-ui/core/Modal';
import { DataGrid, GridColDef, GridRowId, ptBR, GridCellParams } from '@material-ui/data-grid';
import { createMuiTheme, ThemeProvider, makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import Tooltip from '@material-ui/core/Tooltip';
import Fade from '@material-ui/core/Fade';
import ButtonDialog from '@material-ui/core/Button';
import PageviewIcon from '@material-ui/icons/Pageview';
import { useAuth } from '../../hooks/Auth';
import { useLookup } from '../../hooks/Lookup/index';
import IPessoaLookup, { emptyPessoaLookup } from '../../hooks/Lookup/IPessoaLookup';
import { getCNPJMask, getCpfFormated } from '../../utils/getCNPJMask';
import api from '../../services/api';

import {
  ColumnCodigo,
  ColumnNome,
  ColumnNomeFantasia,
  ColumnVinculoPessoa,
  ColumnAccessKey,
  ColumnID,
} from './GridColumns';
import { TitleSearch, ButtonGroup, ButtonSearch, ContainerLookup, ContainerSearch } from './styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: theme.palette.background.paper,
      border: '1px solid #006699',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
  }),
);

const theme = createMuiTheme({ palette: { primary: { main: '#006699' } } }, ptBR);

type functionConfirm = (list: IPessoaLookup[]) => void;
type functionGetTextInput = (value: string) => void;
type getIdPessoa = () => number;

export type InputCustomHandle = {
  clear: () => void;
};

interface ILookupPessoaProps {
  in_id?: number;
  idInputLookup: string;
  vinculoPessoa?: string;
  onIdPessoa?: getIdPessoa;
  onConfirm?: functionConfirm;
  onGetTextInput?: functionGetTextInput;
  multiSelect?: boolean;
  clearInputSearchField?: boolean;
}

const LookupPessoa = forwardRef<InputCustomHandle, ILookupPessoaProps>(
  (
    { in_id, idInputLookup, vinculoPessoa, onIdPessoa, multiSelect, onConfirm, onGetTextInput, clearInputSearchField },
    ref,
  ) => {
    const { user } = useAuth();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [idPessoa, setIdPessoa] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);
    const { getListPessoa, updateListPessoa } = useLookup();
    const [listPessoaRegister, setListPessoaRegister] = useState<IPessoaLookup[]>([]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        const fieldInputSearch = document.getElementById(idInputLookup) as HTMLInputElement | null;
        if (fieldInputSearch) {
          fieldInputSearch.value = '';
        }
        updateListPessoa([]);
        setSelectedIds([]);
      },
    }));

    const ColumnInscricaofederal: GridColDef = {
      field: 'inscricao_federal',
      headerName: 'Inscrição federal',
      width: 200,
      renderCell: (params: GridCellParams) => {
        const localTipo = (params.row as IPessoaLookup).tipo_pessoa ?? '';
        const val = params.value?.toString() ?? '';
        let localLabel = val;
        if (['1-Jurídica', '2-Física'].includes(localTipo))
          localLabel = localTipo === '1-Jurídica' ? getCNPJMask(val) : getCpfFormated(val);
        return <span>{localLabel}</span>;
      },
    };

    const columns: GridColDef[] = [
      ColumnCodigo,
      ColumnNome,
      ColumnInscricaofederal,
      ColumnVinculoPessoa,
      ColumnNomeFantasia,
      ColumnAccessKey,
      ColumnID,
    ];

    const handleOnClickConfirm = useCallback(() => {
      const filterList: IPessoaLookup[] = selectedIds
        .map(id => {
          const item = listPessoaRegister.find(valor => valor.id === id);
          return item ?? emptyPessoaLookup;
        })
        .filter(item => item.id !== 0);

      const fieldInputSearch = document.getElementById(idInputLookup) as HTMLInputElement | null;
      if (fieldInputSearch) {
        if (clearInputSearchField) {
          fieldInputSearch.value = '';
        } else {
          const inputValue = filterList.map(item => `${item.codigo}-${item.nome}`).join();
          if (onGetTextInput) onGetTextInput(inputValue);
          fieldInputSearch.value = inputValue;
        }
      }

      updateListPessoa(filterList);
      setOpen(false);
      if (onConfirm) onConfirm(filterList);
    }, [
      selectedIds,
      listPessoaRegister,
      updateListPessoa,
      onConfirm,
      onGetTextInput,
      clearInputSearchField,
      idInputLookup,
    ]);

    const handleOnClickCancel = useCallback(() => setOpen(false), []);

    const getCadPessoaById = useCallback(
      async (id: number) => {
        if (!id) {
          return;
        }
        const response = await api.post<IPessoaLookup[]>(`/cadpessoas/filter`, { license: user.license.id, id });
        const responseList = response.data;
        setListPessoaRegister(responseList);

        const item = responseList[0] ?? emptyPessoaLookup;
        const fieldInputSearch = document.getElementById(idInputLookup) as HTMLInputElement | null;
        const valor = item.codigo ? `${item.codigo}-${item.nome}` : '';
        if (fieldInputSearch) fieldInputSearch.value = valor;
        updateListPessoa([item]);
        if (onConfirm) onConfirm([item]);
      },
      [user, updateListPessoa, onConfirm, idInputLookup],
    );

    const getInputValue = useCallback(() => {
      const fieldInputSearch = document.getElementById(idInputLookup) as HTMLInputElement | null;
      return fieldInputSearch?.value ?? '';
    }, [idInputLookup]);

    const getVinculoPessoaFilter = useCallback(() => {
      if (vinculoPessoa === '1-Cliente') return ['1-Cliente', '3-Ambos'];
      if (vinculoPessoa === '2-Fornecedor') return ['2-Fornecedor', '3-Ambos'];
      return [];
    }, [vinculoPessoa]);

    const getCadPessoa = useCallback(
      async (cadPessoaNome: string) => {
        let responseList: IPessoaLookup[] = [];
        let id = 0;

        if (onIdPessoa) {
          id = onIdPessoa();
          if (id) {
            const response = await api.post<IPessoaLookup[]>(`/cadpessoas/filter`, { license: user.license.id, id });
            responseList = response.data;
          }
        }

        if (!responseList.length) {
          const response = await api.post<IPessoaLookup[]>(`/cadpessoas/filter`, {
            license: user.license.id,
            nome: cadPessoaNome,
            vinculoPessoa: getVinculoPessoaFilter(),
          });
          responseList = response.data;
        }

        setListPessoaRegister(responseList);
        const list = responseList.map(item => getListPessoa().find(p => p.id === item.id)?.id ?? '').filter(Boolean);
        setSelectedIds(list);
        setOpen(true);
      },
      [onIdPessoa, user, getListPessoa, getVinculoPessoaFilter],
    );

    useEffect(() => {
      if (in_id !== undefined && in_id !== idPessoa) {
        setIdPessoa(in_id);
        getCadPessoaById(in_id);
      }
    }, [idPessoa, in_id, getCadPessoaById]);

    const handleOnClickOpen = useCallback(async () => {
      await getCadPessoa(getInputValue());
    }, [getCadPessoa, getInputValue]);

    const handleOnKeyUpInputSearch = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toUpperCase() === 'ENTER') {
          e.preventDefault();
          getCadPessoa(e.currentTarget.value);
        } else if (e.key.toUpperCase() === 'BACKSPACE') {
          updateListPessoa([]);
        }
      },
      [getCadPessoa, updateListPessoa],
    );

    return (
      <div id={`w2${idInputLookup}87`} style={{ width: '100%' }}>
        <ContainerLookup>
          <ContainerSearch>
            <Tooltip title="Pesquisar pessoa" placement="top">
              <ButtonSearch type="button" onClick={handleOnClickOpen}>
                <PageviewIcon />
              </ButtonSearch>
            </Tooltip>
            <input
              id={idInputLookup}
              type="text"
              placeholder="Pesquisar pessoa..."
              onKeyDown={handleOnKeyUpInputSearch}
            />
          </ContainerSearch>
        </ContainerLookup>

        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          className={classes.modal}
          open={open}
          onClose={handleOnClickConfirm}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
          disableBackdropClick
        >
          <Fade in={open}>
            <div className={classes.paper}>
              <TitleSearch id="transition-modal-title">P E S S O A S</TitleSearch>
              <div style={{ height: '450px', width: '700px' }}>
                <ThemeProvider theme={theme}>
                  <DataGrid
                    headerHeight={40}
                    rowHeight={25}
                    rows={listPessoaRegister}
                    columns={columns}
                    checkboxSelection={multiSelect}
                    selectionModel={selectedIds}
                    getRowId={r => r.id}
                    onSelectionModelChange={e => setSelectedIds(e.selectionModel)}
                    onRowDoubleClick={handleOnClickConfirm}
                  />
                </ThemeProvider>
              </div>
              <ButtonGroup>
                <ButtonDialog onClick={handleOnClickCancel} color="secondary" autoFocus>
                  Cancelar
                </ButtonDialog>
                <ButtonDialog onClick={handleOnClickConfirm} color="primary">
                  Confirmar
                </ButtonDialog>
              </ButtonGroup>
            </div>
          </Fade>
        </Modal>
      </div>
    );
  },
);

export default LookupPessoa;
